'use client';

/**
 * Host adapter contract for the {@code LiveSearchBox} +
 * {@code SearchIconTriggerClient} widgets.
 *
 * <p>Hosts plug in their own search backend (suggest + trending feeds,
 * recents persistence). When no source is mounted the search affordance
 * still renders but submits a plain form to
 * {@code /{tenantSlug}/search?q=...} — no live dropdown, no
 * trending, no recents. Plain-form fallback is the same graceful
 * degradation ThemeMaker offered for no-JS users.
 *
 * <h3>Shape mirrors ThemeMaker's platform/search</h3>
 * <ul>
 *   <li>{@code suggest()} returns full hit objects (title, href, kind,
 *       thumbnail) — used while the user is typing.</li>
 *   <li>{@code trending()} and {@code recents()} return plain query
 *       strings — they're "things to search for", not specific hits.
 *       The dropdown renders them as plain magnifier+text rows.</li>
 * </ul>
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface SearchSuggestion {
  /** Stable id used for React keys. */
  id: string;
  title: string;
  /** Free-form kind id — drives the row icon / thumbnail aspect ratio.
   *  Known values: {@code course}, {@code video}, {@code article},
   *  {@code page}, {@code channel} (avatar — renders square instead of
   *  16:9). */
  kind: string;
  /** Pre-resolved navigation target. Tenant slug already applied by
   *  the source impl. */
  href: string;
  /** Optional thumbnail URL — 16:9 except for {@code kind: 'channel'}
   *  which uses a square avatar. */
  thumbnail?: string;
  /** Optional secondary line — author, duration, etc. */
  description?: string;
}

export interface SearchSource {
  /** Debounced suggestion lookup. Called on every keystroke after the
   *  200ms debounce. Implementations should honour
   *  {@code signal.aborted} and return early when superseded. */
  suggest(opts: {
    query: string;
    kind?: string;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<SearchSuggestion[]>;

  /** Popular query strings shown when the input has focus but no query.
   *  Returns plain text (rendered as "click to search for X"), not hit
   *  data. Implementations may return an empty list when there's no
   *  trending feed. */
  trending(opts?: { kind?: string; limit?: number; signal?: AbortSignal }): Promise<string[]>;

  /** Synchronous read of locally-persisted recent searches (plain
   *  query strings). The widget calls this on mount + focus to seed
   *  the dropdown. */
  recents(opts?: { limit?: number }): string[];

  /** Persist a query string the user just submitted. Called on Enter
   *  and on clicking a suggestion row. Implementations typically
   *  write to localStorage and optionally mirror to a server endpoint
   *  (best-effort, no await needed by the widget). */
  pushRecent(query: string): void;

  /** Optional: clear all recents. The widget surfaces a "Clear" action
   *  in the dropdown header when this is provided. Hosts that lack
   *  server-side recents-clear can implement this as a local-only
   *  wipe (clear localStorage). */
  clearRecents?(): void;
}

const SearchSourceContext = createContext<SearchSource | null>(null);

export function SearchSourceProvider({
  value,
  children,
}: {
  value: SearchSource;
  children: ReactNode;
}) {
  return <SearchSourceContext.Provider value={value}>{children}</SearchSourceContext.Provider>;
}

/** Returns the host-provided source, or {@code null} when no
 *  provider is mounted (widget falls back to plain-form submit). */
export function useSearchSource(): SearchSource | null {
  return useContext(SearchSourceContext);
}
